import React, { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setBreadcrump } from '../../app/organizationReducer';
import { setToastData } from '../../app/toastReducer';
import { HTTP_STATUS } from '../../http/constants/http.status';
import MenuButtonsPortal from '../Menu/MenuButtonsPortal';
import BiButton from '../primitives/buttons/BiButton.primitive';
import InputField from '../shared/Fields/InputField';
import LoaderContainer from '../shared/loaderContainer/LoaderContainer';
import { getUserName, getUserProfiles } from '../../util/admin-utils';
import { capitalize, cloneDeep, compact, filter, find, get, isEmpty, isEqual, uniq } from 'lodash';
import { RouteConstants } from '../../constants/RouteConstants';
import { fieldIsEmpty, validateNameLength } from '../../util/validators';
import { AppConstants } from '../../constants/AppConstants';
import { getFailureMessage } from '../../util/ErrorUtil';
import TabDrawer from '../Reusable/TabDrawer';
import ToastNotification from '../Reusable/ToastNotification';
import PickList, { PICKLIST_TYPES } from '../shared/Fields/PickList';
import CarePlanDetails from './CarePlanDetails';
import Collaborators from './Collaborators';
import Assignment from './CarePlanAssignment';
import {
  createCareplan,
  deleteCareplan,
  getCarePlan,
  updateCareplan
} from '../../http/careplan-services';
import {
  resetCarePlanReducer,
  setCareplanData,
  setConditionsList,
  setOrgList,
  setCareplanConfig,
  setListData
} from '../../app/carePlanReducer';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Modal from '../shared/ModalPortal/Modal';
import { ExclamationIcon } from '@heroicons/react/outline';
import { CalendarIcon } from '@heroicons/react/outline';
import {
  configureCareplanCategories,
  fetchCustomRoleUserList,
  fetchDeviceList,
  fetchPractitionerRole,
  filterEncounters,
  getCarePlanConditions,
  getCarePlanOrganizations,
  getCareTeamsList,
  getCareplanListData,
  getConditionsList,
  getEncounterListData,
  getEncounterListItems
} from './carePlanUtils';
import AuditInfo from '../Common/AuditInfo';
import { checkFeaturePermission } from '../../util/permissionUtils';
import CareplanMenu from './CareplanMenu';
import { MenuConstants } from '../../constants/MenuConstants';

/**
 * @parentId careplan
 * @manager Create, Read, Update, Delete - Careplan
 * @overview Create, Read, Update, Delete - Careplan
 *   <section>
 *       <p>
 *           <b>View Careplan</b><br/>
 *           Clicking on careplan row item on the careplan listing page will take user to view careplan details page in view only mode. Clicking on <b>Back to Careplan list</b> will take the user to careplan(s) listing page.
 *           Details page also includes audit information such as <i>Created By</i>, <i>Created On</i>, <i>Last Modified By</i>, <i>Last Modified On</i>, <i>Start date/time</i> and <i>End date/time</i>. Logged-in user should have the careplan-service.careplan.get permission.
 *           <br/><br/>
 *           <b>Edit Careplan</b><br/>
 *           Editing of Careplan is only possible if the logged in user role API permission has edit permissions. i.e <strong>careplan-service.careplan.patch</strong>. Clicking on <b>Edit</b> button on the admin portal toolbar will change the page into editing mode, which allows users to edit all fields like Title, Intent, Description, Status, Category, Author Category and Author, Contributor Category and Contributor, Add organizations field and clicking on <b>Save</b> button will save the changes. Clicking on <b>Cancel</b> will take the user back to careplan details page in view only mode.
 *           Once a careplan is created, the careplan name cannot be changed.
 *           <br/><br/>
 *           <b>Create Careplan</b><br/>
 *           <b>New Careplan</b> button is only enabled if the logged-in user role API permission has create permission. i.e <strong>careplan-service.careplan.create</strong>
 *           Clicking on <b>New Careplan</b> button on careplan listing page will take user to new careplan creation page. Clicking on <b>Cancel</b> will take the user to careplan listing page.
 *           <br/><br/>
 *           User can create new careplan by entering <b>Title</b>,<b>Description</b> mandatory fields and clicking on <b>Save</b> button on the admin portal toolbar. Other fields are optional.
 *       </p> <br/>
 *   <section>
 *         <p>
 *          <h4>Manage Careplan - Action Menu</h4>
 *         Action Menu Dropdown will have the following options:
 *          <br/>
 *          <table>
 *            <tr>
 *               <th>Name</th>
 *               <th>Description</th>
 *               <th>Permission Needed</th>
 *            </tr>
 *               <tr>
 *               <td>Edit Careplan Details</td>
 *               <td>Changes to Edit mode from Read only mode</td>
 *               <td>careplan-service.careplan.patch</td>
 *            </tr>
 *              <tr>
 *               <td>View Careplan Activities</td>
 *               <td>Navigates to Activities Listing Page for the Careplan template</td>
 *               <td>careplan-service.careplan.activity.list</td>
 *            </tr>
 *             <tr>
 *               <td>Add an activity</td>
 *               <td>Navigates to Create Activity Page for the Careplan template</td>
 *               <td>careplan-service.careplan.activity.add</td>
 *            </tr>
 *             <tr>
 *               <td>View Notes</td>
 *               <td>Navigates to Notes Listing Page for the Careplan Template.</td>
 *               <td>careplan-service.careplan.notes.list</td>
 *            </tr>
 *            <tr>
 *               <td>Add a Note</td>
 *               <td>Navigates to Create Note Page for the Careplan Template.</td>
 *               <td>careplan-service.careplan.notes.create</td>
 *            </tr>
 *          </table>
 *          <br/>
 *        </p>
 * </section> </br>
 * <section>
 *         <p>
 *          <h4>Delete Operation</h4>
 *         The Delete button will be present on the right hand side of the Details page. It is subject to the user having the permission careplan-service.careplan.delete and also the careplan status should not be draft.
 *          <br/>
 *        </p>
 * </section> </br>
 *
 *       <p>
 *          <h4>View/Create/Edit careplan Fields</h4>
 *          Create/View/Edit careplan page will display below fields with following validation constraints.
 *          <br/>
 *          <table>
 *            <tr>
 *               <th>Field Name</th>
 *               <th>Required</th>
 *               <th>Data Type</th>
 *               <th>Field Type</th>
 *               <th>Max Length</th>
 *            </tr>
 *            <tr>
 *               <td>Title</td>
 *               <td>Yes</td>
 *               <td>String</td>
 *               <td>Textfield</td>
 *               <td>1024</td>
 *            </tr>
 *             <tr>
 *               <td>Intent</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>Dropdown</td>
 *               <td>255</td>
 *            </tr>
 *             <tr>
 *               <td>Description</td>
 *               <td>Yes</td>
 *               <td>String</td>
 *               <td>Textfield</td>
 *               <td>4096</td>
 *            </tr>
 *             <tr>
 *               <td>Category</td>
 *               <td>No</td>
 *               <td>CodeableConcept[]</td>
 *               <td>JSON Editor</td>
 *               <td>NA</td>
 *            </tr>
 *              <tr>
 *               <td>Identifier</td>
 *               <td>No</td>
 *               <td>Identifier[]</td>
 *               <td>JSON Editor</td>
 *               <td>NA</td>
 *            </tr>
 *            <tr>
 *               <td>Author Category</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>Dropdown</td>
 *               <td>NA</td>
 *            </tr>
 *              <tr>
 *               <td>Author</td>
 *               <td>Yes (If Author Category Selected)</td>
 *               <td>String(UUID)</td>
 *               <td>Dropdown</td>
 *               <td>NA</td>
 *            </tr>
 *             <tr>
 *               <td>Contributor Category</td>
 *               <td>No</td>
 *               <td>String</td>
 *               <td>Dropdown</td>
 *               <td>NA</td>
 *            </tr>
 *              <tr>
 *               <td>Contributor Type</td>
 *               <td>Yes (If Contributor Category Selected)</td>
 *               <td>String(UUID)</td>
 *               <td>Dropdown</td>
 *               <td>NA</td>
 *            </tr>
 *               <tr>
 *               <td>Add organizations</td>
 *               <td>No</td>
 *               <td>Array of String(UUID)</td>
 *               <td>Dropdown</td>
 *               <td>NA</td>
 *            </tr>
 *          </table>
 *          <br/>
 *     </p>
 *     </section>
 *      <section>
 *         <p>
 *          <h4>Configuration based options</h4>
 *          Careplan dropdown options in the form are configured via the System Setting with category "careplan.template.default.values". User can configure these options in the system settings for the following fields:
 *        <br/>
 *          <table>
 *            <tr>
 *               <th>Name</th>
 *               <th>Key</th>
 *            </tr>
 *              <tr>
 *               <td>Status</td>
 *               <td>careplan.template.status.values</td>
 *            </tr>
 *             <tr>
 *               <td>Intent</td>
 *               <td>careplan.template.intent.values</td>
 *            </tr>
 *               <tr>
 *               <td>Author Category</td>
 *               <td>careplan.template.author.types</td>
 *            </tr>
 *              <tr>
 *               <td>Contributor Category</td>
 *               <td>careplan.template.contributor.types</td>
 *            </tr>
 *          </table>
 *          <br/>
 *        </p>
 *      </section>
 *     <section>
 *     <section>
 *      <h4>Failure Status Codes</h3>
 *       <p>
 *       This section describes Create/Edit notifcate template failure status code information.
 *       <br>
 *       <table>
 *           <tr>
 *               <th>HTTP Status Code</th>
 *               <th>Service Error Code</th>
 *               <th>Error Message</th>
 *           </tr>
 *           <tr>
 *               <td>403</td>
 *               <td>CPS_PERMISSION_DENIED</td>
 *               <td>You do not have permission to view this page.</td>
 *           </tr>
 *           <tr>
 *               <td>400</td>
 *               <td>CPS_ERROR_PARSING</td>
 *               <td>Wrong format of category</td>
 *           </tr>
 *           <tr>
 *               <td>500</td>
 *               <td>CPS_INTERNAL_ERROR</td>
 *               <td>Internal Server Error</td>
 *           </tr>
 *           <tr>
 *               <td>503</td>
 *               <td></td>
 *               <td>Service Unavailable</td>
 *           </tr>
 *       </table>
 *   </p>
 *   </section>
 *   <section>
 *      <h4>Dependent System settings, Platform services & Role Permission</h3>
 *      <p>This section describes the list of dependent system settings & platform services required for functioning of create/edit/view careplan page.</p>
 *       <h5>System Settings</h4>
 *       <p>Table lists all the dependent system setting(s) defined in configuration service with either global/organization scope.</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Key</th>
 *               <th>Type</th>
 *               <th>Value</th>
 *               <th>Scope</th>
 *           </tr>
 *           <tr>
 *               <td>None</td>
 *               <td>None</td>
 *               <td>None</td>
 *               <td>None</td>
 *           </tr>
 *       </table>
 *       <br>
 *      <h5>Platform Service(s)</h4>
 *      <p>Table lists all the dependent platform service(s) with specific version(s) for create/View/Edit careplan page.</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Service Name</th>
 *               <th>Version</th>
 *           </tr>
 *           <tr>
 *               <td>Careplan Service</td>
 *               <td>1.3.0</td>
 *           </tr>
 *           <tr>
 *               <td>User Management Service</td>
 *               <td>1.6.0</td>
 *           </tr>
 *         <tr>
 *               <td>Configuration Service</td>
 *               <td>1.6.0</td>
 *         </tr>
 *       </table>
 *       <br>
 *      <h5>API Role Permission(s)</h4>
 *      <p>Table lists the required API careplan permissions for creating metadata application page</p>
 *       <br>
 *       <table>
 *           <tr>
 *               <th>Feature</th>
 *               <th>API URL</th>
 *               <th>API Method</th>
 *               <th>API Permission</th>
 *               <th>Required</th>
 *           </tr>
 *           <tr>
 *               <td>Create Careplan</td>
 *               <td>/careplans</td>
 *               <td>POST</td>
 *               <td>careplan-service.careplan.create</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Get Careplan by ID</td>
 *               <td>/careplans/{careplan-id}</td>
 *               <td>GET</td>
 *               <td>careplan-service.careplan.get</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Patch careplan</td>
 *               <td>/careplans/{careplan-id}</td>
 *               <td>PATCH</td>
 *               <td>careplan-service.careplan.patch</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Delete Careplan</td>
 *               <td>/careplans/{careplan-id}</td>
 *               <td>DELETE</td>
 *               <td>careplan-service.careplan.delete</td>
 *               <td>No</td>
 *           </tr>
 *       </table>
 *   </section>
 *   <section>
 *    <section>
 *    <p>Sequence Diagram for Careplan Creation, View and Update Page</p>
 *   </section>
 *   </section>
 */

export interface ChildFormProps {
  isEdit: boolean;
  formData: any;
  setFormData: any;
  formError: any;
  setFormError: any;
}

const formDataSchema: any = {
  title: '',
  intent: '',
  description: '',
  category: '',
  identifier: '',
  encounter: '',
  authorCategory: [],
  author: [],
  contributorCategory: [],
  periodStart: '',
  periodEnd: '',
  contributor: [],
  careTeam: [],
  conditions: [],
  organization: [],
  status: []
};

const formErrorSchema: any = {
  description: '',
  author: '',
  contributor: '',
  periodStart: '',
  periodEnd: '',
  category: '',
  identifier: ''
};

const CarePlanTemplate = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [disableButton, setDisableButton] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  // const [activeTab, setActiveTab] = useState(`${t('T_CARE_PLAN_DETAILS_HEADER')}`);
  const [auditData, setAuditData] = useState({
    createdBy: '',
    updatedBy: '',
    updatedOn: '',
    createdOn: '',
    periodStart: '',
    periodEnd: '',
    createdByOrg: ''
  });
  const [selectedOption, setSelectedOption] = useState('');

  const [formData, updateFormData] = useState<any>(formDataSchema);

  const [tempDate, setTempDate] = useState({
    periodStart: '',
    periodEnd: ''
  });

  const [formDataClone, setFormDataClone] = useState<any>(formDataSchema);

  const [formError, updateFormError] = useState(formErrorSchema);
  // const encounterListData: any = useAppSelector((state) => state.carePlanData.encounterList) || [];
  const careplanData = useAppSelector((state) => state.carePlanData.careplanData);
  const carePlanOrgList: any = useAppSelector((state) => state.carePlanData.orgList) || [];
  // const deviceList: any = useAppSelector((state) => state.carePlanData.deviceList) || [];
  // const conditionsListData: any =
  //   useAppSelector((state) => state.carePlanData.conditionsList) || [];
  const careplanConfig: any = useAppSelector((state) => state.carePlanData.careplanConfig) || {};
  const listData: any = useAppSelector((state) => state.carePlanData.listData) || {};

  const toastData = useAppSelector((state) => state.toast);

  const setFormData = (payload: any) => {
    let clonedFormData: any = cloneDeep(formData);
    Object.keys(payload).forEach((key) => {
      clonedFormData[key] = payload[key];
    });

    updateFormData(clonedFormData);
  };

  const setFormError = (payload: any) => {
    let clonedFormError: any = cloneDeep(formError);
    Object.keys(payload).forEach((key) => {
      clonedFormError[key] = payload[key];
    });

    updateFormError(clonedFormError);
  };

  useEffect(() => {
    // fetchEncounterData();

    if (!params?.id) {
      dispatch(setCareplanData({}));

      setIsEdit(true);
      dispatch(
        setBreadcrump([
          {
            key: RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_CREATE,
            label: `${t('T_NEW_CAREPLAN')}`
          }
        ])
      );
      if (formData?.title) {
        dispatch(resetCarePlanReducer());
      }
      setIsLoading(false);
    } else {
      verifyEditData();
    }
  }, []);

  useEffect(() => {
    if (isEmpty(careplanConfig) && !configLoaded) {
      checkConfiguration();
    }
  }, [careplanConfig, configLoaded]);

  const checkConfiguration = async () => {
    let config = await configureCareplanCategories();
    if (!isEmpty(config)) {
      dispatch(setCareplanConfig(config));
    } else {
      setConfigLoaded(true);
    }
  };

  // const fetchEncounterData = async () => {
  //   if (!encounterListData.length) {
  //     const encounterData = await getEncounterListData();
  //     if (encounterData.length) {
  //       dispatch(setEncounterList(filterEncounters(encounterData)));
  //     }
  //   }
  // };

  const verifyEditData = async () => {
    async function init() {
      try {
        let response = await getCarePlan(params?.id);
        const { status, data } = response;

        if (HTTP_STATUS.isSuccess(status)) {
          dispatch(setCareplanData(data));
        } else {
          dispatch(
            setToastData({
              toastMessage: getFailureMessage(response),
              isToastActive: true,
              toastType: 'error'
            })
          );
          setIsLoading(false);
          navigate(-1);
        }
      } catch (e) {
        dispatch(
          setToastData({
            isToastActive: true,
            toastMessage: t('T_ERROR_SOMETHING_WRONG'),
            toastType: 'error'
          })
        );
        setIsLoading(false);
        navigate(-1);
      }
    }

    if (
      (params?.id && !isEmpty(careplanData) && params.id !== careplanData?.id) ||
      (isEmpty(careplanData) && params?.id)
    ) {
      await init();
    }
  };

  useEffect(() => {
    const init = async () => {
      const listDataClone: any = cloneDeep(listData);
      const populateList = async (selection: any = {}) => {
        const { key, apiConfig } = selection || {};
        const data = await getCareplanListData(apiConfig);
        if (data.length) {
          listDataClone[key] = data;
        }
      };
      const {
        id,
        title,
        intent,
        description,
        category,
        identifier,
        status,
        createdBy,
        createdOn,
        updatedOn,
        updatedBy,
        periodEnd,
        periodStart,
        authorType,
        authorId,
        contributor,
        careteamIds,
        conditionIds,
        organizationIds,
        encounterId
      } = careplanData;

      let payload: any = {};
      payload.title = title;

      let intentEle = find(careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_INTENT_VALUES], {
        key: intent
      });
      payload.intent =
        intent && !intentEle
          ? [{ display: capitalize(intent), key: intent, value: intent }]
          : intentEle
          ? [intentEle]
          : [];

      payload.description = description || '';

      payload.category = category?.length ? JSON.stringify(category) : '';
      payload.identifier = identifier?.length ? JSON.stringify(identifier) : '';

      let statusEle = find(careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_STATUS_VALUES], {
        key: status
      });
      payload.status = statusEle ? [statusEle] : [];

      if (authorType?.length) {
        let authorTypeEle = find(
          careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_AUTHOR_TYPES],
          { key: authorType }
        );

        const { apiConfig } = authorTypeEle || {};
        const { fieldKey = '' } = apiConfig || {};

        if (!listDataClone?.[authorType]?.length) {
          await populateList(authorTypeEle);
        }

        let authorEle = find(listDataClone?.[authorType], { [fieldKey]: authorId });
        payload.author = [authorEle];
        payload.authorCategory = [authorTypeEle];
      } else {
        payload.author = [];
        payload.authorCategory = [];
      }

      if (contributor?.length) {
        let pillsPayload = [];

        for (const item of contributor) {
          let obj: any = {};

          const { id, type } = item || {};

          obj.id = id;
          obj.key = type;

          let contributorTypeEle = find(
            careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_CONTRIBUTOR_TYPES],
            {
              key: type
            }
          );

          const { apiConfig, display = '' } = contributorTypeEle || {};
          const { fieldKey = '', fieldValues = [] } = apiConfig || {};

          try {
            if (!listDataClone?.[type]?.length) {
              await populateList(contributorTypeEle);
            }

            let contributorEle = find(listDataClone?.[type], {
              [fieldKey]: id
            });

            obj.label = display;
            const valueKey = get(contributorTypeEle, 'apiConfig.fieldValues[0]', '');
            obj.name = get(contributorEle, valueKey) || '';
            pillsPayload.push(obj);
          } catch (error) {
            continue;
          }
        }
        payload.contributor = pillsPayload;
      } else {
        payload.contributor = [];
      }

      // if (careteamIds?.length) {
      //   if (!listData?.care_team?.length) {
      //     await populateList('care_team');
      //   }

      //   let careteams = filter(listData?.care_team, (obj: any) => careteamIds.includes(obj.id));

      //   payload.careTeam = careteams?.length ? careteams : [];
      // }

      // if (conditionIds?.length) {
      //   let conditionsListClone = cloneDeep(conditionsListData);
      //   if (!conditionsListClone?.length) {
      //     let listResponse = await getCarePlanConditions();
      //     conditionsListClone = listResponse;
      //     dispatch(setConditionsList(listResponse));
      //   }
      //   let conditions = filter(conditionsListClone, (obj: any) => conditionIds.includes(obj.id));
      //   payload.conditions = conditions?.length ? getConditionsList(conditions) : [];
      // }

      if (organizationIds?.length) {
        let orgListClone = cloneDeep(carePlanOrgList);
        if (!orgListClone?.length) {
          let listResponse = await getCarePlanOrganizations();
          orgListClone = listResponse;
          dispatch(setOrgList(listResponse));
        }

        let organizations = filter(orgListClone, (obj: any) => organizationIds.includes(obj.id));
        payload.organization = organizations?.length ? organizations : [];
      } else {
        payload.organization = [];
      }

      // if (encounterId?.length) {
      //   if (!listData?.encounters?.length) {
      //     await populateList('encounter');
      //   }

      //   let encounter = filter(listData?.encounters, (obj: any) => encounterId === obj?.id);

      //   payload.encounter = encounter?.length ? getEncounterListItems(encounter) : [];
      // }

      // const userInfoMap: Map<string, any> = await getAuditInfo();

      setAuditData({
        createdBy,
        updatedBy,
        updatedOn,
        createdOn,
        periodStart: periodStart
          ? moment(periodStart).format(AppConstants.DEFAULT_DATE_FORMAT)
          : '',
        periodEnd: periodEnd ? moment(periodEnd).format(AppConstants.DEFAULT_DATE_FORMAT) : '',
        createdByOrg: ''
      });

      setFormData(payload);
      setFormDataClone({
        ...formDataClone,
        ...payload,
        category: payload?.category?.length
          ? JSON.stringify(JSON.parse(payload.category), undefined, 4)
          : '',
        identifier: payload?.identifier?.length
          ? JSON.stringify(JSON.parse(payload.identifier), undefined, 4)
          : ''
      });

      dispatch(
        setBreadcrump([
          {
            key: RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_VIEW.replace(':id', id),
            label: `${payload?.title}`
          }
        ])
      );
      if (!carePlanOrgList?.length) {
        dispatch(setOrgList(listData.organization));
      }

      if (!isEqual(listData, listDataClone)) {
        dispatch(setListData(listDataClone));
      }

      setIsLoading(false);
    };

    if (!isEmpty(careplanData) && params?.id && (!isEmpty(careplanConfig) || configLoaded)) {
      init();
    }
  }, [careplanData, careplanConfig, configLoaded]);

  const validateFormData = () => {
    let errors: any = cloneDeep(formError);
    errors['title'] = fieldIsEmpty(formData.title.trim())
      ? t('T_VALIDATION_INPUT_ERR_EMPTY_MSG')
      : validateNameLength(formData.title.trim(), 1024)
      ? ''
      : t('T_VALIDATION_ERR_TEXT_TOO_LONG_MSG');

    // errors['contributor'] =
    //   formData?.contributorCategory?.length && !formData?.contributor?.length
    //     ? t('T_CARE_PLAN_CONTRIBUTOR_SELECTION_REQUIRED')
    //     : '';

    // errors['author'] =
    //   formData?.authorCategory?.length && !formData?.author?.length
    //     ? t('T_CARE_PLAN_AUTHOR_SELECTION_REQUIRED')
    //     : '';

    errors['description'] = fieldIsEmpty(formData?.description?.trim())
      ? ''
      : validateNameLength(formData.description.trim(), 4096)
      ? ''
      : t('T_VALIDATION_ERR_TEXT_LENGTH_MSG_4096');

    //const { title, description, author, contributor } = errors;

    // if (!title.length && !description.length && (author.length || contributor.length)) {
    //   setActiveTab(`${t('T_CARE_PLAN_COLLABORATORS')}`);
    // }
    setFormError(errors);
    return errors;
  };

  const validator = () => {
    if (params?.id && isEqual(formData, formDataClone)) {
      return true;
    }

    if (disableButton) {
      return disableButton;
    }
    const { title, description, status } = formData;
    if (!title?.length || !description?.length) {
      return true;
    }
    if (formData?.category?.length && formError?.category?.length) {
      return true;
    }
    if (formData?.identifier?.length && formError?.identifier?.length) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (disableButton) {
      setDisableButton(false);
    }
  }, [formData]);

  // const tabConfig = [
  //   {
  //     label: t('T_CARE_PLAN_DETAILS_HEADER'),
  //     component: <CarePlanDetails isEdit={isEdit}></CarePlanDetails>
  //   },
  //   {
  //     label: t('T_CARE_PLAN_COLLABORATORS'),
  //     component: <Collaborators isEdit={isEdit}></Collaborators>
  //   },
  //   {
  //     label: t('T_CARE_PLAN_ASSIGNMENT'),
  //     component: <Assignment isEdit={isEdit}></Assignment>
  //   }
  // ];

  const deleteCareplanAPI = async () => {
    setIsLoading(true);
    let response = await deleteCareplan(params?.id || careplanData?.id);

    if ([HTTP_STATUS.HTTP_CREATED, HTTP_STATUS.HTTP_OK].includes(response.status)) {
      dispatch(
        setToastData({
          toastMessage: t('T_CARE_PLAN_DELETED_SUCCESS').replace('%s', formData?.title || ''),
          isToastActive: true,
          toastType: 'success'
        })
      );

      navigate(RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_LIST);
    } else {
      setIsLoading(false);
      dispatch(
        setToastData({
          toastMessage: getFailureMessage(response),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
  };
  const deleteCareplanUI = () => {
    return (
      <div>
        <div>{t('T_CARE_PLAN_DELETE_PROMPT')}</div>

        <div className="mt-8 text-right">
          <BiButton
            className={'mr-5 border border-solid border-primary text-primary'}
            type="button"
            testId="delete-careplan-btn"
            onClick={() => {
              setSelectedOption('');
            }}
          >
            {t('T_CANCEL')}
          </BiButton>

          <BiButton
            className={
              'border border-solid border-red-600 text-danger hover:bg-red-600 hover:text-white'
            }
            type="button"
            onClick={() => {
              setSelectedOption('');
              deleteCareplanAPI();
            }}
          >
            {t('T_YES_DELETE')}
          </BiButton>
        </div>
      </div>
    );
  };

  const savePayload = async () => {
    const editMode = params?.id?.length || false;
    const errors = validateFormData();
    if (
      Object.keys(errors).length !==
      Object.keys(errors).filter((validateError) => {
        return errors[validateError] === '';
      }).length
    ) {
      setDisableButton(true);
      return;
    }

    setIsLoading(true);
    const {
      title,
      intent,
      description,
      category,
      identifier,
      // encounter,
      authorCategory,
      author,
      // contributorCategory,
      contributor,
      // careTeam,
      conditions,
      organization,
      status,
      periodStart,
      periodEnd
    } = formData;

    let payload: any = {
      title,
      status: status?.[0]?.key || AppConstants.CAREPLAN_STATUS_DEFAULT,
      description
      // encounterId: ''
    };

    payload.category = category.length ? JSON.parse(category) : [];
    payload.identifier = identifier.length ? JSON.parse(identifier) : [];
    // }
    if (intent?.length) {
      const [selection] = intent;
      payload.intent = selection?.key || '';
    } else {
      payload.intent = '';
    }

    if (authorCategory?.length) {
      const [selectedType] = authorCategory;
      const { apiConfig } = selectedType || {};
      const { fieldKey = 'id' } = apiConfig;
      const [selectedAuthor] = author;
      payload.authorType = selectedType?.key || '';
      payload.authorId = selectedAuthor?.[fieldKey];
    } else {
      payload.authorType = '';
    }

    // if (encounter?.length) {
    //   const [encounterItem] = encounter;
    //   payload.encounterId = encounterItem?.key || '';
    // }

    // if (contributorCategory?.length) {
    //   const [selectedCategory] = contributorCategory;
    //   const { apiConfig } = selectedCategory || {};
    //   const { fieldKey = 'id' } = apiConfig;
    //   const [selectedContributor] = contributor;
    //   if (selectedContributor?.[fieldKey] && selectedCategory?.key) {
    //     let contributorObject = {
    //       id: selectedContributor?.[fieldKey],
    //       type: selectedCategory?.key
    //     };
    //     payload.contributor = [contributorObject];
    //   }
    // } else {
    //   payload.contributor = [];
    // }

    if (contributor && contributor.length) {
      payload.contributor = contributor.map((item: any) => {
        let obj: any = {};
        if (item.key) {
          obj.type = item.key;
        }
        if (item.id) {
          obj.id = item.id;
        }
        return obj;
      });
    }

    // if (careTeam?.length) {
    //   payload.careteamIds = careTeam.map((careTeams: any) => careTeams?.id);
    // }

    if (conditions?.length) {
      payload.conditionIds = conditions.map((condition: any) => condition?.key);
    } else {
      payload.conditionIds = [];
    }

    if (organization?.length) {
      payload.organizationIds = organization.map((org: any) => org?.id);
    } else {
      payload.organizationIds = [];
    }

    if (periodStart && editMode) {
      payload.periodStart = moment(periodStart).format(AppConstants.CAREPLAN_DATE_FORMAT);
    }

    if (periodEnd && editMode) {
      payload.periodEnd = moment(periodEnd).format(AppConstants.CAREPLAN_DATE_FORMAT);
    }

    try {
      if (!editMode) {
        let response = await createCareplan(payload);
        const { data, status } = response;
        if (HTTP_STATUS.isSuccess(status)) {
          dispatch(
            setToastData({
              toastMessage: t('T_CARE_PLAN_CREATED_SUCCESS').replace('%s', title),
              isToastActive: true,
              toastType: 'success'
            })
          );
          navigate(RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_LIST);
        } else {
          setIsLoading(false);
          dispatch(
            setToastData({
              toastMessage: getFailureMessage(response),
              isToastActive: true,
              toastType: 'error'
            })
          );
        }
      } else {
        let response = await updateCareplan(params?.id || careplanData?.id, payload);
        const { data, status } = response;
        if (HTTP_STATUS.isSuccess(status)) {
          dispatch(
            setToastData({
              toastMessage: t('T_CARE_PLAN_UPDATED_SUCCESS').replace('%s', payload.title),
              isToastActive: true,
              toastType: 'success'
            })
          );
          setIsLoading(false);
          setIsEdit(false);
        } else {
          setIsLoading(false);
          dispatch(
            setToastData({
              toastMessage: getFailureMessage(response),
              isToastActive: true,
              toastType: 'error'
            })
          );
        }
      }
    } catch (e: any) {
      setIsLoading(false);
      dispatch(
        setToastData({
          toastMessage: t('T_SERVER_ERROR'),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
  };

  const toggleStatus = (ev: any) => {
    switch (ev.toLowerCase()) {
      case 'active':
        setSelectedOption('activate');
        break;
      case 'draft':
        setSelectedOption('draft');
        break;
      case 'unknown':
        setSelectedOption('unknown');
        break;
    }
  };

  const DateInputField = forwardRef(
    ({ onClick, formError, disabled, placeholder, label, fieldKey, overrideValue }: any, ref) => (
      <InputField
        overrideValue={overrideValue}
        className="w-full"
        type="text"
        id="max-word-count"
        label={label}
        placeholder={placeholder}
        fieldKey={fieldKey}
        required={false}
        formData={formData}
        setFormData={setFormData}
        formError={formError}
        disabled={disabled}
        onClick={onClick}
        ref={ref}
        onBlurhandler={(e: any) => {}}
        handleChange={() => {}}
        CustomIcon={CalendarIcon}
      />
    )
  );

  const setStartandEnd = () => {
    let periodStart = tempDate.periodStart || formData?.periodStart;
    let periodEnd = tempDate.periodEnd || formData?.periodEnd;

    const handleDateChange = (date: any, key: string) => {
      const updatedDate = new Date(date);
      let updatedData = { ...tempDate, [key]: updatedDate.toISOString() };
      setTempDate(updatedData);
    };

    const handleTimeChange = (time: any, key: string) => {
      const updatedTime = new Date(time);
      let updatedData = { ...tempDate, [key]: updatedTime.toISOString() };
      setTempDate(updatedData);
    };

    const checkTimeValidation = (key: string) => {
      let periodStart = tempDate.periodStart;
      let periodEnd = tempDate.periodEnd;

      if (
        key === 'periodStart' &&
        periodStart &&
        periodEnd &&
        new Date(periodStart) > new Date(periodEnd)
      ) {
        return true;
      }

      if (
        key === 'periodEnd' &&
        periodStart &&
        periodEnd &&
        new Date(periodEnd) < new Date(periodStart)
      ) {
        return true;
      }
      return false;
    };

    return (
      <div>
        <div className="mt-4 flex w-full flex-row justify-between">
          <div className="w-[49%]">
            <DatePicker
              selected={periodStart ? new Date(periodStart) : null}
              onChange={(date: any) => handleDateChange(date, 'periodStart')}
              fixedHeight
              // minDate={new Date()}
              showMonthDropdown
              showYearDropdown
              scrollableYearDropdown
              showDisabledMonthNavigation
              shouldCloseOnSelect={false}
              yearDropdownItemNumber={100}
              calendarClassName={'text-base'}
              customInput={
                <DateInputField
                  overrideValue={
                    periodStart
                      ? moment(periodStart).format(AppConstants.DEFAULT_CAREPLAN_DATE_FORMAT)
                      : ''
                  }
                  fieldKey="periodStart"
                  formError={{
                    periodStart: checkTimeValidation('periodStart')
                      ? t('T_PERIOD_START_VALIDATION')
                      : ''
                  }}
                  label={t('T_CARE_PLAN_START_DATE')}
                />
              }
            />
          </div>
          <div className="w-[49%]">
            <DatePicker
              selected={periodStart ? new Date(periodStart) : null}
              onChange={(date: any) => handleTimeChange(date, 'periodStart')}
              fixedHeight
              showTimeSelect
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              showTimeSelectOnly
              calendarClassName={'text-base'}
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="h:mm aa"
              customInput={
                <DateInputField
                  overrideValue={
                    periodStart
                      ? moment(periodStart).format(AppConstants.DEFAULT_CAREPLAN_TIME_FORMAT)
                      : ''
                  }
                  fieldKey="periodStart"
                  formError={{ periodStart: '' }}
                  label={t('T_CARE_PLAN_START_TIME')}
                />
              }
            />
          </div>
        </div>
        <div className="mt-4 flex w-full flex-row justify-between">
          <div className="w-[49%]">
            <DatePicker
              selected={periodEnd ? new Date(periodEnd) : null}
              onChange={(date: any) => handleDateChange(date, 'periodEnd')}
              fixedHeight
              showMonthDropdown
              showYearDropdown
              scrollableYearDropdown
              minDate={periodStart ? new Date(periodStart) : undefined}
              yearDropdownItemNumber={100}
              showDisabledMonthNavigation
              shouldCloseOnSelect={false}
              calendarClassName={'text-base'}
              customInput={
                <DateInputField
                  overrideValue={
                    periodEnd
                      ? moment(periodEnd).format(AppConstants.DEFAULT_CAREPLAN_DATE_FORMAT)
                      : ''
                  }
                  fieldKey="periodEnd"
                  formError={{
                    periodEnd: checkTimeValidation('periodEnd') ? t('T_PERIOD_END_VALIDATION') : ''
                  }}
                  label={t('T_CARE_PLAN_END_DATE')}
                />
              }
            />
          </div>
          <div className="w-[49%]">
            <DatePicker
              selected={periodEnd ? new Date(periodEnd) : null}
              onChange={(date: any) => handleTimeChange(date, 'periodEnd')}
              minDate={periodStart ? new Date(periodStart) : undefined}
              fixedHeight
              showTimeSelect
              showTimeSelectOnly
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              calendarClassName={'text-base'}
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="h:mm aa"
              customInput={
                <DateInputField
                  overrideValue={
                    periodEnd
                      ? moment(periodEnd).format(AppConstants.DEFAULT_CAREPLAN_TIME_FORMAT)
                      : ''
                  }
                  fieldKey="periodEnd"
                  formError={{ periodEnd: '' }}
                  label={t('T_CARE_PLAN_END_TIME')}
                />
              }
            />
          </div>
        </div>

        <div className="mt-8 text-right">
          <BiButton
            className={'mr-5 border border-solid border-primary text-primary'}
            type="button"
            testId={`careplan-date-cancel-btn`}
            onClick={() => {
              setTempDate({ periodStart: '', periodEnd: '' });
              setSelectedOption('');
            }}
          >
            {t('T_CANCEL')}
          </BiButton>

          <BiButton
            className={'border border-solid border-white bg-primary text-white'}
            type="button"
            disabled={checkTimeValidation('periodStart') || checkTimeValidation('periodEnd')}
            onClick={() => {
              setSelectedOption('');
              // editTemplate();
              setFormData({
                periodStart: tempDate.periodStart || formData.periodStart,
                periodEnd: tempDate.periodEnd || formData.periodEnd
              });
              setAuditData({
                ...auditData,
                periodStart: moment(tempDate.periodStart || formData.periodStart).format(
                  AppConstants.DEFAULT_DATE_FORMAT
                ),
                periodEnd: moment(tempDate.periodEnd || formData.periodEnd).format(
                  AppConstants.DEFAULT_DATE_FORMAT
                )
              });
              setTempDate({ periodStart: '', periodEnd: '' });
            }}
          >
            {t('T_ACTIVATE')}
          </BiButton>
        </div>
      </div>
    );
  };

  const getStatusConfigValue = (key: string, status: string) => {
    const value = find(careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_STATUS_VALUES], {
      key: key
    });
    return value?.config?.[status] || '';
  };

  const onMenuDropDownClick = (key: string) => {
    if (key === MenuConstants.MENU_CARE_PLAN_EDIT_DETAILS) {
      setIsEdit(true);
    }
    if (key === MenuConstants.MENU_CARE_PLAN_NOTES_CREATE) {
      dispatch(setCareplanData(careplanData));
      navigate(
        `${RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_NOTES_CREATE.replace(':id', careplanData?.id)}`
      );
    }

    if (key === MenuConstants.MENU_CARE_PLAN_ACTIVITIES_CREATE) {
      dispatch(setCareplanData({ ...careplanData }));
      navigate(RouteConstants.ROUTE_CARE_PLAN_ACTIVITIES_CREATE.replace(':id', careplanData?.id));
    }

    if (key === MenuConstants.MENU_CARE_PLAN_ACTIVITIES) {
      dispatch(setCareplanData({ ...careplanData }));
      navigate(RouteConstants.ROUTE_CARE_PLAN_ACTIVITIES.replace(':id', careplanData?.id));
    }
    if (key === MenuConstants.MENU_CARE_PLAN_NOTES) {
      dispatch(setCareplanData(careplanData));
      navigate(
        `${RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_NOTES_LIST.replace(':id', careplanData?.id)}`
      );
    }
  };

  return (
    <LoaderContainer isLoading={isLoading}>
      <div className="flex h-full w-4/5  flex-col overflow-visible">
        <MenuButtonsPortal>
          <>
            <BiButton
              className={'mr-2 text-primary'}
              type="button"
              onClick={() => {
                if (params.id) {
                  if (isEdit) {
                    setIsEdit(false);
                    setFormData(formDataClone);
                    setFormError(formErrorSchema);
                  } else {
                    navigate(RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_LIST);
                  }
                } else {
                  navigate(RouteConstants.ROUTE_CARE_PLAN_TEMPLATE_LIST);
                }
              }}
            >
              {params?.id && !isEdit ? t('T_CARE_PLAN_LIST_BACK') : t('T_CANCEL')}
            </BiButton>
            {isEdit ? (
              <BiButton
                className={'bg-primary text-white'}
                type="button"
                onClick={savePayload}
                disabled={validator()}
              >
                {t('T_SAVE')}
              </BiButton>
            ) : (
              <CareplanMenu
                dropdownMenu
                disabled={!params?.id}
                onOptionClick={(key: string) => onMenuDropDownClick(key)}
              />
            )}
          </>
        </MenuButtonsPortal>
        <ToastNotification
          message={toastData.toastMessage}
          isActive={toastData.isToastActive}
          setIsActive={() =>
            dispatch(
              setToastData({
                toastMessage: '',
                isToastActive: false,
                toastType: null
              })
            )
          }
          status={toastData.toastType}
        />

        <Modal
          showCloseIcon={true}
          width={'50rem'}
          headerText={t('T_CARE_PLAN_DATE_PICKER')}
          toggleVisiblity={(value: boolean) => {
            setSelectedOption('');
          }}
          isVisible={['activate'].includes(selectedOption)}
          body={setStartandEnd()}
          headerLeftIcon={
            selectedOption === 'delete' ? <ExclamationIcon className="h-7.5 w-6" /> : ''
          }
        />

        <Modal
          showCloseIcon={true}
          width={'30rem'}
          headerText={t('T_CONFIRM')}
          toggleVisiblity={(value: boolean) => {
            setSelectedOption('');
          }}
          isVisible={['delete'].includes(selectedOption)}
          body={deleteCareplanUI()}
          headerLeftIcon={
            selectedOption === 'delete' ? <ExclamationIcon className="h-7.5 w-6" /> : ''
          }
        />

        <div className="border-1 rounded-md border-solid border-gray-300 p-4">
          {/* <TabDrawer
            borderBottom={true}
            tabConfig={tabConfig}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          /> */}
          <CarePlanDetails
            isEdit={isEdit}
            formData={formData}
            formError={formError}
            setFormError={setFormError}
            setFormData={setFormData}
          ></CarePlanDetails>
          <Collaborators
            isEdit={isEdit}
            formData={formData}
            formError={formError}
            setFormError={setFormError}
            setFormData={setFormData}
          ></Collaborators>
          <Assignment
            isEdit={isEdit}
            formData={formData}
            setFormData={setFormData}
            formError={formError}
            setFormError={setFormError}
          ></Assignment>
        </div>
      </div>

      <div className="flex w-1/5 flex-col pl-5 pt-5">
        <>
          {params?.id && !isLoading && (
            <div className="w-60">
              <>
                <PickList
                  fieldKey={'key'}
                  label={t('T_STATUS')}
                  required={false}
                  fieldValues={['display']}
                  selectedItems={formData?.status}
                  onSelectedChange={(item: any) => {
                    setFormData({ status: item });
                  }}
                  items={
                    careplanConfig?.[AppConstants.CAREPLAN_CONFIG_TEMPLATE_STATUS_VALUES] || []
                  }
                  disabled={!isEdit}
                  readOnly={false}
                  hasMore={false}
                  type={PICKLIST_TYPES.SINGLE}
                  style={
                    isEdit && !isEmpty(careplanConfig) && formData?.status
                      ? {
                          backgroundColor: getStatusConfigValue(formData?.status?.[0]?.key, 'color')
                        }
                      : {}
                  }
                />

                <div className="mt-0.5 text-sm" style={{ color: '#8C8C8C' }}>
                  {t(getStatusConfigValue(formData?.status?.[0]?.key, 'helper'))}
                </div>
              </>
            </div>
          )}
          {isEdit && params?.id && formData?.status?.[0]?.key === 'active' && (
            <div className="mt-6">
              <BiButton
                disabled={!isEdit}
                className={'w-60 border border-solid border-[#0074E0] bg-[#DCEEFE] text-[#0074E0]'}
                type="button"
                onClick={() => {
                  toggleStatus('active');
                }}
              >
                {t('T_SURVEY_SET_DATES')}
              </BiButton>
            </div>
          )}
          {params?.id && (
            <div className="mt-6">
              <>
                {(auditData?.createdOn || auditData?.createdBy) && (
                  <div>
                    <AuditInfo
                      label={'CREATED'}
                      date={auditData?.createdOn || ''}
                      user={auditData?.createdBy || ''}
                    />
                  </div>
                )}
                {(auditData?.updatedOn || auditData?.updatedBy) && (
                  <div>
                    <AuditInfo
                      label={'LAST_MODIFIED'}
                      date={auditData?.updatedOn || ''}
                      user={auditData?.updatedBy || ''}
                    />
                  </div>
                )}
                {auditData.periodStart && (
                  <div className="my-2.5">
                    <p>{t('T_START_DATE_TIME')}</p>
                    <p className="font-light">{auditData.periodStart}</p>
                  </div>
                )}
                {auditData.periodEnd && (
                  <div className="my-2.5">
                    <p>{t('T_END_DATE_TIME')}</p>
                    <p className="font-light">{auditData.periodEnd}</p>
                  </div>
                )}
                {auditData.createdByOrg && (
                  <div className="mt-2 w-3/5 text-sm text-[#595959]">
                    <p>{t('T_CREATED_BY_ORG')}</p>
                    <p>{auditData.createdByOrg}</p>
                  </div>
                )}
              </>
            </div>
          )}
          {formData?.status !== 'draft' &&
          params?.id &&
          checkFeaturePermission(AppConstants.FEATURE_KEYS.CAREPLAN_MANAGEMENT_DELETE, false) ? (
            <div className="mt-6">
              <BiButton
                className={
                  'w-60 border border-solid border-red-600 bg-white text-danger hover:bg-red-600 hover:text-white'
                }
                type="button"
                onClick={() => {
                  setSelectedOption('delete');
                }}
              >
                {t('T_DELETE')}
              </BiButton>
            </div>
          ) : (
            ''
          )}
        </>
      </div>
    </LoaderContainer>
  );
};

export default CarePlanTemplate;
